import { Shoot } from '../types'
import { CalendarEvent } from '../types/availability'

// Utility function for formatting dates for API calls
export const formatDateForApi = (date: Date): string => {
  const year = date.getFullYear()
  const month = String(date.getMonth() + 1).padStart(2, '0')
  const day = String(date.getDate()).padStart(2, '0')
  return `${year}-${month}-${day}`
}

// ICS file format utilities
export const generateICSContent = (events: Shoot[]): string => {
  const lines = [
    'BEGIN:VCALENDAR',
    'VERSION:2.0',
    'PRODID:-//FreelanceFlow//Calendar//EN',
    'CALSCALE:GREGORIAN',
    'METHOD:PUBLISH'
  ]

  events.forEach(shoot => {
    const startDateTime = formatDateTimeForICS(shoot.date, shoot.start_time)
    const endDateTime = formatDateTimeForICS(shoot.date, shoot.end_time)
    const uid = `shoot-${shoot.id}@freelanceflow.app`
    
    lines.push(
      'BEGIN:VEVENT',
      `UID:${uid}`,
      `DTSTART:${startDateTime}`,
      `DTEND:${endDateTime}`,
      `SUMMARY:${escapeICSText(shoot.title)}`,
      `DESCRIPTION:${escapeICSText(generateDescription(shoot))}`,
      shoot.location ? `LOCATION:${escapeICSText(shoot.location)}` : '',
      `STATUS:${shoot.status === 'scheduled' ? 'CONFIRMED' : 'TENTATIVE'}`,
      `CREATED:${formatDateTimeForICS(shoot.created_at?.split('T')[0] || shoot.date, '00:00')}`,
      `LAST-MODIFIED:${formatDateTimeForICS(shoot.updated_at?.split('T')[0] || shoot.date, '00:00')}`,
      'END:VEVENT'
    )
  })

  lines.push('END:VCALENDAR')
  return lines.filter(line => line).join('\r\n')
}

export const downloadICSFile = (events: Shoot[], filename: string = 'freelance-calendar.ics'): void => {
  const icsContent = generateICSContent(events)
  const blob = new Blob([icsContent], { type: 'text/calendar;charset=utf-8' })
  const url = URL.createObjectURL(blob)
  
  const link = document.createElement('a')
  link.href = url
  link.download = filename
  document.body.appendChild(link)
  link.click()
  document.body.removeChild(link)
  URL.revokeObjectURL(url)
}

export const generateCalendarUrl = (events: Shoot[]): string => {
  // Generate a data URL for calendar import
  const icsContent = generateICSContent(events)
  return `data:text/calendar;charset=utf-8,${encodeURIComponent(icsContent)}`
}

// Helper functions
const formatDateTimeForICS = (date: string, time: string): string => {
  // Convert YYYY-MM-DD and HH:MM to YYYYMMDDTHHMMSSZ
  const [year, month, day] = date.split('-')
  const [hours, minutes] = time.split(':')
  return `${year}${month}${day}T${hours}${minutes}00Z`
}

const escapeICSText = (text: string): string => {
  return text
    .replace(/\\/g, '\\\\')
    .replace(/;/g, '\\;')
    .replace(/,/g, '\\,')
    .replace(/\n/g, '\\n')
    .replace(/\r/g, '')
}

const generateDescription = (shoot: Shoot): string => {
  let description = `${shoot.type === 'photo' ? 'Photo' : 'Video'} shoot`
  
  if (shoot.client?.name) {
    description += ` with ${shoot.client.name}`
  }
  
  if (shoot.notes) {
    description += `\\n\\nNotes: ${shoot.notes}`
  }
  
  description += `\\n\\nScheduled via FreelanceFlow`
  return description
}

// Parse ICS content for import
export const parseICSContent = (icsContent: string): CalendarEvent[] => {
  const events: CalendarEvent[] = []
  const lines = icsContent.split(/\r?\n/)
  
  let currentEvent: Partial<CalendarEvent> | null = null
  
  for (const line of lines) {
    if (line.startsWith('BEGIN:VEVENT')) {
      currentEvent = {
        id: '',
        title: '',
        start: new Date(),
        end: new Date(),
        type: 'shoot',
        source: 'local',
        editable: true
      }
    } else if (line.startsWith('END:VEVENT') && currentEvent) {
      if (currentEvent.id && currentEvent.title) {
        events.push(currentEvent as CalendarEvent)
      }
      currentEvent = null
    } else if (currentEvent) {
      const [key, ...valueParts] = line.split(':')
      const value = valueParts.join(':')
      
      switch (key) {
        case 'UID':
          currentEvent.id = value
          break
        case 'SUMMARY':
          currentEvent.title = unescapeICSText(value)
          break
        case 'DTSTART':
          currentEvent.start = parseICSDateTime(value)
          break
        case 'DTEND':
          currentEvent.end = parseICSDateTime(value)
          break
        case 'DESCRIPTION':
          currentEvent.description = unescapeICSText(value)
          break
        case 'LOCATION':
          currentEvent.location = unescapeICSText(value)
          break
      }
    }
  }
  
  return events
}

const parseICSDateTime = (icsDateTime: string): Date => {
  // Parse YYYYMMDDTHHMMSSZ format
  const year = parseInt(icsDateTime.substr(0, 4))
  const month = parseInt(icsDateTime.substr(4, 2)) - 1 // Month is 0-indexed
  const day = parseInt(icsDateTime.substr(6, 2))
  const hours = parseInt(icsDateTime.substr(9, 2))
  const minutes = parseInt(icsDateTime.substr(11, 2))
  const seconds = parseInt(icsDateTime.substr(13, 2))
  
  return new Date(Date.UTC(year, month, day, hours, minutes, seconds))
}

const unescapeICSText = (text: string): string => {
  return text
    .replace(/\\n/g, '\n')
    .replace(/\\,/g, ',')
    .replace(/\\;/g, ';')
    .replace(/\\\\/g, '\\')
}

// Recurring event utilities
export const generateRecurringEvents = (
  baseEvent: Partial<CalendarEvent>,
  pattern: {
    type: 'daily' | 'weekly' | 'monthly'
    interval: number
    endDate?: Date
    occurrences?: number
  }
): CalendarEvent[] => {
  const events: CalendarEvent[] = []
  
  if (!baseEvent.start || !baseEvent.end) return events
  
  let currentDate = new Date(baseEvent.start)
  let count = 0
  const maxOccurrences = pattern.occurrences || 52 // Default to 1 year of weekly events
  
  while (count < maxOccurrences) {
    if (pattern.endDate && currentDate > pattern.endDate) break
    
    const event: CalendarEvent = {
      id: `${baseEvent.id || 'recurring'}-${count}`,
      title: baseEvent.title || 'Recurring Event',
      start: new Date(currentDate),
      end: new Date(currentDate.getTime() + (baseEvent.end.getTime() - baseEvent.start.getTime())),
      type: baseEvent.type || 'shoot',
      source: baseEvent.source || 'local',
      editable: baseEvent.editable ?? true,
      description: baseEvent.description,
      location: baseEvent.location,
      color: baseEvent.color
    }
    
    events.push(event)
    
    // Calculate next occurrence
    switch (pattern.type) {
      case 'daily':
        currentDate.setDate(currentDate.getDate() + pattern.interval)
        break
      case 'weekly':
        currentDate.setDate(currentDate.getDate() + (7 * pattern.interval))
        break
      case 'monthly':
        currentDate.setMonth(currentDate.getMonth() + pattern.interval)
        break
    }
    
    count++
  }
  
  return events
} 